import {useMutation, useQuery} from "@apollo/client";
import isFunction from "lodash/isFunction";
import omit from "lodash/omit";
import {useSearchParams} from "react-router";

import {
  GQL_INTEGRATION_SAVE,
  GQL_INTEGRATIONS_FORM_DEPENDENCY_QUERY,
  GQL_INTEGRATIONS_PAGE_QUERY,
  GQL_INTEGRATIONS_QUERY,
  IntegrationFormDependencyResult,
  SaveIntegrationResult,
} from "@/lib/queries/integrations.ts";
import {ResponseStatus} from "@/lib/types/common.ts";
import {IntegrationFormData} from "@/lib/types/integrations.ts";
import {EditIntegrationModal} from "@/pages/integrations/edit/EditIntegrationModal.tsx";
import {getIntegrationModuleInfo, integrationListToForm} from "@/pages/integrations/integrations.types.ts";

interface EditIntegrationActionProps {
  formId: string;
  show: boolean;
  setShow: (show: boolean) => void;
  integration?: IntegrationFormData;
  setNewIntegration?: (newIntegration: IntegrationFormData) => void;
}

export const EditIntegrationModalAction = ({
  formId,
  show,
  setShow,
  integration,
  setNewIntegration,
}: EditIntegrationActionProps) => {
  const [searchParams] = useSearchParams();
  const [saveIntegration, {data}] = useMutation<SaveIntegrationResult>(GQL_INTEGRATION_SAVE, {
    refetchQueries: [GQL_INTEGRATIONS_QUERY],
    context: {
      noToast: true,
    },
  });

  const {data: dependencyData} = useQuery<IntegrationFormDependencyResult>(GQL_INTEGRATIONS_FORM_DEPENDENCY_QUERY);

  const handleSubmit = async (data: IntegrationFormData, isDirty: boolean) => {
    const prunedData = omit(data, ["is_errored"]);
    const resp = await saveIntegration({
      variables: {data: prunedData},
      refetchQueries: [GQL_INTEGRATIONS_PAGE_QUERY, GQL_INTEGRATIONS_FORM_DEPENDENCY_QUERY],
    });
    if (resp.data?.response?.status === ResponseStatus.ERROR) {
      return;
    }
    if (isFunction(setNewIntegration) && (!integration?.id || isDirty)) {
      setNewIntegration(integrationListToForm(resp!.data!.response.data));
    }
    setShow(false);
  };

  if (!dependencyData) return null;

  const serverErrors = data?.response?.status === ResponseStatus.ERROR ? data?.response?.message : undefined;

  const moduleInfo = getIntegrationModuleInfo(integration?.module);

  return (
    <EditIntegrationModal
      defaultValues={integration}
      availableContactGroups={dependencyData?.availableContactGroups}
      title={
        <>
          {moduleInfo?.logo && (
            <img
              src={moduleInfo.logo}
              alt={moduleInfo.label}
              height={22}
              width={22}
              className={"pulse-border-radius-xl me-3"}
            />
          )}

          {`${integration?.id ? "Edit" : "Add"} ${moduleInfo?.label ?? "New"} Integration`}
        </>
      }
      formId={formId}
      show={show}
      hide={() => {
        setShow(false);
        if (searchParams.get("gobackoncancel") && history) {
          history.back();
        }
      }}
      onSubmit={handleSubmit}
      serverErrors={serverErrors}
    />
  );
};
